import React, { useState, useEffect } from 'react'
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  GoogleAuthProvider,
} from 'firebase/auth'
import { auth, googleProvider } from '../firebase'
import { getBrandImage } from '../data/brandImages'
import { trackEvento, EVENTOS } from '../utils/analytics'

/**
 * Mensajes de error legibles — nunca mostrar códigos Firebase al cliente.
 */
const ERROR_LEGIBLE = {
  'auth/wrong-password':                     'La contraseña es incorrecta. Verificá e intentá de nuevo.',
  'auth/user-not-found':                     'No encontramos una cuenta con ese email.',
  'auth/invalid-email':                      'El formato del email no es válido.',
  'auth/too-many-requests':                  'Demasiados intentos fallidos. Esperá unos minutos e intentá de nuevo.',
  'auth/user-disabled':                      'Esta cuenta está deshabilitada. Contactá a Maja Morena.',
  'auth/invalid-credential':                 'Email o contraseña incorrectos.',
  'auth/popup-closed-by-user':               'Cerraste la ventana de Google antes de completar el ingreso.',
  'auth/popup-blocked':                      'El navegador bloqueó la ventana de Google. Permitila e intentá de nuevo.',
  'auth/cancelled-popup-request':            null, // silencioso
  'auth/network-request-failed':             'Sin conexión. Revisá tu internet e intentá de nuevo.',
}

function mensajeError(code) {
  return ERROR_LEGIBLE[code] || 'Ocurrió un error inesperado. Intentá de nuevo.'
}

/**
 * Estado de vinculación: cuando el email de Google ya existe con otro proveedor,
 * ofrecemos vincular en lugar de crear una segunda cuenta.
 */
const LINK_STATE = {
  NONE:     'none',
  OFFERING: 'offering',   // mostramos oferta al usuario
  PASSWORD: 'password',   // pedimos contraseña para vincular
}

const ClientAccess = ({ onLogin, setCurrentView, siteConfig }) => {
  const [form, setForm]         = useState({ email: '', password: '' })
  const [errorMsg, setErrorMsg] = useState('')
  const [resetEmail, setResetEmail] = useState('')
  const [showReset, setShowReset]   = useState(false)
  const [resetMsg, setResetMsg]     = useState('')
  const [cargando, setCargando]     = useState(false)

  // Estado de vinculación para el caso email-ya-existe-con-otro-provider
  const [linkState, setLinkState]         = useState(LINK_STATE.NONE)
  const [pendingCredential, setPendingCredential] = useState(null)
  const [linkPassword, setLinkPassword]   = useState('')
  const [linkEmail, setLinkEmail]         = useState('')

  const logo = getBrandImage(siteConfig, 'logo')
  const hero = getBrandImage(siteConfig, 'heroInicio')

  useEffect(() => { trackEvento(EVENTOS.LOGIN_VIEW) }, [])

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setErrorMsg('')
  }

  // ─── Login email/contraseña ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMsg('')
    trackEvento(EVENTOS.LOGIN_EMAIL_STARTED)
    setCargando(true)
    try {
      await signInWithEmailAndPassword(auth, form.email, form.password)
      trackEvento(EVENTOS.LOGIN_EMAIL_SUCCESS)
      onLogin('home', 'client')
    } catch (err) {
      trackEvento(EVENTOS.LOGIN_EMAIL_FAILED, { code: err.code })
      setErrorMsg(mensajeError(err.code))
    } finally {
      setCargando(false)
    }
  }

  // ─── Google Sign-In ───────────────────────────────────────────────────────
  const handleGoogle = async () => {
    setErrorMsg('')
    trackEvento(EVENTOS.LOGIN_GOOGLE_STARTED)
    setCargando(true)
    try {
      const result = await signInWithPopup(auth, googleProvider)
      // onAuthStateChanged en App.js detectará si hay doc en usuarios/ o no
      trackEvento(EVENTOS.LOGIN_GOOGLE_SUCCESS)
      onLogin('home', 'client')
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        // El email ya existe con otro proveedor (email/password u otro)
        // Guardamos la credencial pendiente y ofrecemos vincular
        const credential = GoogleAuthProvider.credentialFromError(err)
        const email = err.customData?.email || ''
        setPendingCredential(credential)
        setLinkEmail(email)
        setLinkState(LINK_STATE.OFFERING)
        trackEvento(EVENTOS.ACCOUNT_LINK_OFFERED, { metodo: 'google' })
        setCargando(false)
        return
      }
      if (err.code !== 'auth/cancelled-popup-request') {
        trackEvento(EVENTOS.LOGIN_GOOGLE_FAILED, { code: err.code })
        const msg = mensajeError(err.code)
        if (msg) setErrorMsg(msg)
      }
    } finally {
      setCargando(false)
    }
  }

  // ─── Vincular Google a cuenta existente ──────────────────────────────────
  const handleLinkAccount = async (e) => {
    e.preventDefault()
    if (!linkPassword) { setErrorMsg('Ingresá tu contraseña para vincular las cuentas.'); return }
    setCargando(true)
    setErrorMsg('')
    try {
      // 1. Autenticar con email/password existente
      const result = await signInWithEmailAndPassword(auth, linkEmail, linkPassword)
      // 2. Vincular la credencial Google a esa cuenta
      await linkWithCredential(result.user, pendingCredential)
      // 3. Actualizar authProviders en Firestore
      // (lo hace onAuthStateChanged → App.js detectará el usuario y refreshUserProfile)
      trackEvento(EVENTOS.ACCOUNT_LINK_COMPLETED)
      setLinkState(LINK_STATE.NONE)
      setPendingCredential(null)
      onLogin('home', 'client')
    } catch (err) {
      trackEvento(EVENTOS.LOGIN_EMAIL_FAILED, { code: err.code, contexto: 'link' })
      setErrorMsg(mensajeError(err.code))
    } finally {
      setCargando(false)
    }
  }

  // ─── Recuperar contraseña ─────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault()
    if (!resetEmail) { setErrorMsg('Ingresá tu email para recuperar el acceso.'); return }
    trackEvento(EVENTOS.PASSWORD_RECOVERY_STARTED)
    try {
      await sendPasswordResetEmail(auth, resetEmail)
      trackEvento(EVENTOS.PASSWORD_RECOVERY_SENT)
      setResetMsg('Te enviamos un email para restablecer tu contraseña.')
      setShowReset(false)
      setResetEmail('')
    } catch (err) {
      setErrorMsg('No pudimos enviar el email. Verificá que esté bien escrito.')
    }
  }

  // ─── UI: oferta de vinculación ────────────────────────────────────────────
  if (linkState === LINK_STATE.OFFERING || linkState === LINK_STATE.PASSWORD) {
    return (
      <div className="grid lg:grid-cols-2 min-h-[78vh] mx-3 sm:mx-6 rounded-[2rem] overflow-hidden bg-white/80 border border-black/5">
        <div className="hidden lg:block relative min-h-[520px]">
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <p className="absolute bottom-8 left-8 right-8 text-white font-script text-3xl">
            Hacemos tu vida más rica
          </p>
        </div>
        <div className="flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <img src={logo} alt="Maja Morena" className="h-16 mx-auto mb-4" />
            <h2 className="text-center font-display text-3xl tracking-tight">Ya tenés cuenta Maja</h2>
            <p className="mt-2 text-center text-sm text-maja-ink/60">
              Ya existe una cuenta con <b>{linkEmail}</b>.
              Ingresá tu contraseña para vincular Google a tu cuenta existente.
            </p>
            <form className="mt-6 space-y-4" onSubmit={handleLinkAccount}>
              <div className="px-4 py-3 bg-black/5 rounded-2xl text-sm text-maja-ink/70">
                {linkEmail}
              </div>
              <input
                type="password"
                placeholder="Tu contraseña actual"
                value={linkPassword}
                onChange={(e) => { setLinkPassword(e.target.value); setErrorMsg('') }}
                className="w-full h-12 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red"
                required
              />
              {errorMsg && <p className="text-sm text-maja-red">{errorMsg}</p>}
              <button type="submit" disabled={cargando}
                className="w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-60">
                {cargando ? 'Vinculando...' : 'Vincular mi cuenta con Google'}
              </button>
              <button type="button"
                onClick={() => { setLinkState(LINK_STATE.NONE); trackEvento(EVENTOS.ACCOUNT_LINK_REJECTED) }}
                className="w-full h-11 rounded-full border border-black/10 text-maja-ink font-semibold">
                Cancelar
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // ─── UI: login normal ─────────────────────────────────────────────────────
  return (
    <div className="grid lg:grid-cols-2 min-h-[78vh] mx-3 sm:mx-6 rounded-[2rem] overflow-hidden bg-white/80 border border-black/5">
      <div className="hidden lg:block relative min-h-[520px]">
        <img src={hero} alt="Local Maja Morena" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <p className="absolute bottom-8 left-8 right-8 text-white font-script text-3xl">
          Hacemos tu vida más rica
        </p>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <img src={logo} alt="Maja Morena" className="h-16 mx-auto mb-4" />
          <h2 className="text-center font-display text-4xl tracking-tight">Iniciar sesión</h2>
          <p className="mt-2 text-center text-sm text-maja-ink/60">
            Entrá a Maja Club con tu cuenta
          </p>

          {/* Google */}
          <button
            onClick={handleGoogle}
            disabled={cargando}
            className="mt-6 w-full h-12 rounded-full border border-black/15 bg-white font-semibold text-maja-ink flex items-center justify-center gap-3 hover:bg-black/5 disabled:opacity-60 transition"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.9 2.3 30.3 0 24 0 14.7 0 6.7 5.4 2.8 13.3l7.8 6.1C12.5 13 17.8 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17z" />
              <path fill="#FBBC05" d="M10.6 28.6A14.4 14.4 0 0 1 9.5 24c0-1.6.3-3.1.7-4.6l-7.8-6.1A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l8.1-6.2z"/>
              <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.2 0-11.5-4.2-13.4-9.9l-8.1 6.2C6.7 42.6 14.7 48 24 48z"/>
            </svg>
            Continuar con Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/8" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-maja-ink/30">o</span>
            <div className="flex-1 h-px bg-black/8" />
          </div>

          {/* Email/contraseña */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <input name="email" type="email" required
              placeholder="Email"
              value={form.email} onChange={handleChange}
              className="w-full h-12 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red"
            />
            <input name="password" type="password" required
              placeholder="Contraseña"
              value={form.password} onChange={handleChange}
              className="w-full h-12 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red"
            />
            {errorMsg && <p className="text-sm text-maja-red">{errorMsg}</p>}
            {resetMsg  && <p className="text-sm text-emerald-700">{resetMsg}</p>}
            <button type="submit" disabled={cargando}
              className="w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-60">
              {cargando ? 'Ingresando...' : 'Acceder'}
            </button>
          </form>

          <div className="text-center mt-4">
            <button onClick={() => setShowReset(!showReset)}
              className="text-sm font-semibold text-maja-red">
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          {showReset && (
            <form onSubmit={handleReset} className="mt-4 space-y-2">
              <input type="email" placeholder="Tu email"
                className="w-full h-11 px-4 rounded-2xl border border-black/10"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)} />
              <button type="submit"
                className="w-full h-11 rounded-full bg-maja-ink text-white font-semibold">
                Enviar email de recuperación
              </button>
            </form>
          )}

          <p className="text-center text-sm text-maja-ink/60 mt-6">
            ¿Aún no sos miembro?{' '}
            <button onClick={() => setCurrentView('registration')}
              className="font-semibold text-maja-red">
              Quiero sumarme
            </button>
          </p>

          <div className="mt-8 pt-6 border-t border-black/5 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-maja-ink/35 mb-3">
              Personal Maja Morena
            </p>
            <button onClick={() => setCurrentView('operatorLogin')}
              className="h-11 px-6 rounded-full border border-black/10 font-semibold text-maja-ink hover:border-maja-red">
              Ingreso de personal
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientAccess
